import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ERROR_CODES } from "@/lib/error-messages";
import {
  BankIdData,
  CheckoutStep,
  DeliveryData,
  PaymentData,
} from "@/lib/types/checkout-steps";
import { OrderResponse } from "../types/order";
import {
  fetchOrder,
  finalizeOrder,
  addToCart,
  updateOrder,
} from "../thunks/order.thunks";

type LoadingState = "idle" | "pending" | "fulfilled" | "rejected";

export type StepStates = {
  [CheckoutStep.BANK_ID]: {
    completed: boolean;
    data: BankIdData;
  };
  [CheckoutStep.DELIVERY]: {
    completed: boolean;
    data: DeliveryData;
  };
  [CheckoutStep.PAYMENT]: {
    completed: boolean;
    data: PaymentData;
  };
};

interface OrderState {
  initializeComplete: boolean;
  fetchedOrder: {
    data: OrderResponse | null;
    loading: boolean;
    error: string | null;
  };
  addToCartLoading: boolean;
  addToCartError: string | null;
  createOrder: {
    loading: boolean;
    error: string | null;
  };
  submitLoading: boolean;
  steps: {
    currentStep: CheckoutStep;
    stepStates: StepStates;
  };
  updateOrder: {
    loading: LoadingState;
    error: string | null;
  };
}

const initialState: OrderState = {
  initializeComplete: false,
  fetchedOrder: {
    loading: false,
    data: null,
    error: null,
  },
  addToCartLoading: false,
  addToCartError: null,
  createOrder: {
    loading: false,
    error: null,
  },
  submitLoading: false,
  steps: {
    currentStep: CheckoutStep.BANK_ID,
    stepStates: {
      [CheckoutStep.BANK_ID]: {
        completed: false,
        data: {
          name: "",
          personalNumber: "",
        },
      },
      [CheckoutStep.DELIVERY]: {
        completed: false,
        data: {
          deliveryMethod: "pickup",
          selectedParcelBox: null,
          selectedServicePoint: null,
        },
      },
      [CheckoutStep.PAYMENT]: {
        completed: false,
        data: {
          orderId: "",
        },
      },
    },
  },
  updateOrder: {
    loading: "idle",
    error: null,
  },
};

const orderSlice = createSlice({
  name: "order",
  initialState,
  reducers: {
    resetOrder: () => initialState,
    setInitializeComplete: (state) => {
      state.initializeComplete = true;
    },
    setSubmitLoading: (state, action: PayloadAction<boolean>) => {
      state.submitLoading = action.payload;
    },
    setCreatingOrder: (state, action: PayloadAction<boolean>) => {
      state.createOrder.loading = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.steps.currentStep = action.payload;
    },
    completeBankIdStep: (state, action: PayloadAction<BankIdData>) => {
      state.steps.stepStates[CheckoutStep.BANK_ID] = {
        completed: true,
        data: action.payload,
      };
    },
    completeDeliveryStep: (state, action: PayloadAction<DeliveryData>) => {
      state.steps.stepStates[CheckoutStep.DELIVERY] = {
        completed: true,
        data: action.payload,
      };
    },
    completePaymentStep: (state, action: PayloadAction<PaymentData>) => {
      state.steps.stepStates[CheckoutStep.PAYMENT] = {
        completed: true,
        data: action.payload,
      };
    },
    editStep: (state, action: PayloadAction<CheckoutStep>) => {
      state.steps.currentStep = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrder.pending, (state) => {
        state.fetchedOrder.loading = true;
        state.fetchedOrder.error = null;
      })
      .addCase(fetchOrder.fulfilled, (state, action) => {
        state.fetchedOrder.loading = false;
        state.fetchedOrder.data = action.payload;
      })
      .addCase(fetchOrder.rejected, (state) => {
        state.fetchedOrder.loading = false;
        state.fetchedOrder.error = ERROR_CODES.FAILED_TO_FETCH_ORDER;
      })
      .addCase(finalizeOrder.pending, (state) => {
        state.createOrder.loading = true;
        state.createOrder.error = null;
      })
      .addCase(finalizeOrder.fulfilled, () => {
        // Do nothing to delay redirect
      })
      .addCase(finalizeOrder.rejected, (state, action) => {
        state.createOrder.loading = false;
        // Use backend message when available (from thrown Error), else fallback
        state.createOrder.error =
          (action.error?.message as string) ||
          ERROR_CODES.FAILED_TO_CREATE_ORDER;
      })
      .addCase(addToCart.pending, (state) => {
        state.addToCartLoading = true;
        state.addToCartError = null;
      })
      .addCase(addToCart.fulfilled, (state) => {
        state.addToCartLoading = false;
      })
      .addCase(addToCart.rejected, (state) => {
        state.addToCartLoading = false;
        state.addToCartError = ERROR_CODES.FAILED_TO_ADD_TO_CART;
      })
      .addCase(updateOrder.pending, (state) => {
        state.updateOrder.loading = "pending";
        state.updateOrder.error = null;
      })
      .addCase(updateOrder.fulfilled, (state) => {
        state.updateOrder.loading = "fulfilled";
      })
      .addCase(updateOrder.rejected, (state) => {
        state.updateOrder.loading = "rejected";
        state.updateOrder.error = ERROR_CODES.FAILED_TO_UPDATE_ORDER;
      });
  },
});

export const {
  resetOrder,
  setSubmitLoading,
  setCreatingOrder,
  setInitializeComplete,
  setCurrentStep,
  completeBankIdStep,
  completeDeliveryStep,
  completePaymentStep,
  editStep,
} = orderSlice.actions;

export default orderSlice.reducer;
